
import React, { useState } from 'react';
import { useStore } from '../../hooks/useStore.ts';
import { api } from '../../services/api.ts';
import { ShieldAlert, Trash2, Clock, Calendar, CheckCircle2, AlertTriangle, Sparkles, Loader2 } from 'lucide-react';
import { ComplianceRisk, ComplianceRequirement, Task } from '../../types.ts';
import { refineRiskMitigation } from '../../services/geminiService.ts';
import { useToast } from '../../hooks/useToast.ts';

export const ComplianceSection: React.FC<{ projectId: number }> = ({ projectId }) => {
  const project = useStore(() => api.projects.get(projectId), [projectId], 'projects');
  const risks = useStore(() => api.risks.list(r => r.projectId === projectId), [projectId], 'risks') as ComplianceRisk[] | undefined;
  const compliance = useStore(() => api.complianceRequirements.list(c => c.projectId === projectId), [projectId], 'complianceRequirements') as ComplianceRequirement[] | undefined;
  const checks = useStore(() => api.checklistItems.list(c => c.projectId === projectId), [projectId], 'checklistItems') as Task[] | undefined;

  const { addToast } = useToast();
  const [refiningId, setRefiningId] = useState<number | null>(null);

  const now = new Date().getTime();

  const handleRefineMitigation = async (risk: ComplianceRisk) => {
    if (!risk.id || !project) return;
    setRefiningId(risk.id);
    try {
      const refinedPlan = await refineRiskMitigation(project.title, risk.summary, risk.mitigationPlan);
      await api.risks.update(risk.id, { mitigationPlan: refinedPlan });
      addToast({ type: 'success', title: 'Strategy Refined', message: 'Risk mitigation plan updated with AI-optimized directives.' });
    } catch (err) {
      addToast({ type: 'error', title: 'Refinement Failed', message: 'Intelligence engine encountered an error.' });
    } finally {
      setRefiningId(null);
    }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-16">
        <div className="space-y-12">
             <div className="px-4">
                <h3 className="text-2xl font-black text-black tracking-tight flex items-center gap-4">
                   <Clock className="text-black" size={28}/> Compliance Schedule
                </h3>
                <p className="text-xs font-bold text-zinc-500 uppercase tracking-widest mt-2">Contractual Milestone Log</p>
             </div>
             <div className="space-y-6">
                {compliance?.sort((a,b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime()).map(c => {
                   const due = new Date(c.dueDate).getTime();
                   const daysLeft = Math.ceil((due - now) / (1000 * 60 * 60 * 24));
                   const isUrgent = daysLeft < 7 && c.status !== 'Completed';

                   return (
                      <div key={c.id} className={`p-8 bg-white border rounded-[2.5rem] shadow-sm transition-all ${isUrgent ? 'border-amber-200 bg-amber-50/30' : 'border-zinc-200'}`}>
                         <div className="flex justify-between items-start mb-6">
                            <div className="flex items-center gap-3">
                               <div className={`p-2 rounded-xl ${isUrgent ? 'bg-amber-100 text-amber-600' : 'bg-zinc-100 text-zinc-400'}`}>
                                  {isUrgent ? <AlertTriangle size={18}/> : <Calendar size={18}/>}
                               </div>
                               <div>
                                  <h4 className="font-black text-black text-lg">{c.title}</h4>
                                  <span className="text-[10px] font-black uppercase tracking-widest text-zinc-400">{c.requirementType} Obligation</span>
                               </div>
                            </div>
                            <div className="text-right">
                               <div className={`text-xl font-black ${isUrgent ? 'text-amber-600' : 'text-zinc-400'}`}>
                                  {daysLeft <= 0 ? 'DUE' : `${daysLeft}d`}
                               </div>
                               <div className="text-[9px] font-black uppercase text-zinc-400 tracking-widest">Remains</div>
                            </div>
                         </div>
                         <p className="text-sm font-medium text-zinc-600 leading-relaxed mb-6 italic">"{c.description}"</p>
                         <div className="flex justify-between items-center pt-6 border-t border-zinc-100">
                            <div className="flex gap-4">
                               <button 
                                 onClick={() => api.complianceRequirements.update(c.id!, { status: c.status === 'Completed' ? 'Pending' : 'Completed' })}
                                 className={`px-6 py-2 rounded-full text-[10px] font-black uppercase tracking-widest transition-all ${
                                    c.status === 'Completed' ? 'bg-emerald-500 text-white' : 'bg-zinc-100 text-zinc-400 hover:text-black hover:bg-zinc-200'
                                 }`}
                               >
                                  {c.status === 'Completed' ? 'Compliant' : 'Mark Fulfilled'}
                               </button>
                            </div>
                            <button onClick={() => api.complianceRequirements.delete(c.id!)} className="text-zinc-200 hover:text-rose-500 transition-colors"><Trash2 size={20}/></button>
                         </div>
                      </div>
                   )
                })}
                {(!compliance || compliance.length === 0) && (
                   <div className="py-20 text-center border-4 border-dashed border-zinc-100 rounded-[3rem]">
                      <p className="text-zinc-400 font-black uppercase tracking-widest text-xs">No deadlines registered.</p>
                   </div>
                )}
             </div>
        </div>

        <div className="space-y-12">
             <div>
                <h3 className="text-2xl font-black text-black tracking-tight flex items-center gap-4"><ShieldAlert className="text-black" size={28}/> Profit & Liability Register</h3>
                <p className="text-xs font-bold text-zinc-500 uppercase tracking-widest mt-2">Operational Hurdle Monitoring</p>
             </div>
             <div className="space-y-6">
               {risks?.map(r => (
                 <div key={r.id} className="p-8 bg-white border border-zinc-200 rounded-[2.5rem] shadow-sm hover:shadow-2xl transition-all duration-500 group">
                    <div className="flex flex-col w-full">
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex items-center gap-3">
                          <span className={`w-3 h-3 rounded-full ${r.level === 'High' || r.level === 'Critical' ? 'bg-rose-500 animate-pulse' : 'bg-amber-500'}`}></span>
                          <h4 className="font-black text-black text-lg">{r.summary}</h4>
                        </div>
                        <div className="flex gap-2">
                          <button 
                            onClick={() => handleRefineMitigation(r)}
                            disabled={refiningId === r.id}
                            className={`p-2 rounded-xl transition-all border ${
                              refiningId === r.id ? 'bg-black text-white' : 'text-zinc-400 border-zinc-100 hover:text-black hover:border-black'
                            }`}
                            title="AI Refine Strategy"
                          >
                            {refiningId === r.id ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16}/>}
                          </button>
                          <button onClick={() => api.risks.delete(r.id!)} className="p-2 text-zinc-200 hover:text-rose-500 transition-colors">
                            <Trash2 size={20}/>
                          </button>
                        </div>
                      </div>
                      <div className="bg-zinc-50 p-6 rounded-2xl border border-zinc-100/50">
                        <span className="text-[8px] font-black uppercase text-zinc-400 tracking-[0.2em] mb-2 block">AI Defense Strategy</span>
                        <p className="text-sm font-medium text-zinc-600 leading-relaxed italic">"{r.mitigationPlan || 'Use Sparkles to generate recovery plan.'}"</p>
                      </div>
                    </div>
                 </div>
               ))}
               {(!risks || risks.length === 0) && (
                   <div className="py-20 text-center border-4 border-dashed border-zinc-100 rounded-[3rem]">
                      <p className="text-zinc-400 font-black uppercase tracking-widest text-xs">Register clear.</p>
                   </div>
                )}
             </div>
        </div>
    </div>
  );
};
